import {
  NotificationIcon as CoreNotificationIcon,
  useSession,
} from '@zhiyue/core';
import React from 'react';
import { getOpenPlatformToken } from '@/services/bff/open-platform.api';
import { hasOAuth2Session } from '@/services/oauth2';

type NotificationIconProps = Omit<
  React.ComponentProps<typeof CoreNotificationIcon>,
  'token'
>;

const TOKEN_REFRESH_SKEW_MS = 60_000;
const TOKEN_REFRESH_RETRY_MS = 15_000;
const LEGACY_TOKEN_REFRESH_INTERVAL_MS = 60_000;
const MIN_TOKEN_REFRESH_DELAY_MS = 1_000;

const NotificationIcon: React.FC<NotificationIconProps> = (props) => {
  const { currentUser, loading } = useSession();
  const [token, setToken] = React.useState<string>();

  React.useEffect(() => {
    if (loading || !currentUser?.memberUuid || !hasOAuth2Session()) {
      setToken(undefined);
      return;
    }

    let active = true;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRefresh = (delay: number) => {
      refreshTimer = setTimeout(refreshToken, delay);
    };

    const refreshToken = async () => {
      try {
        const result = await getOpenPlatformToken();
        if (!active) return;

        setToken(result.accessToken);
        const refreshDelay = Number.isFinite(result.expiresAt)
          ? (result.expiresAt as number) - Date.now() - TOKEN_REFRESH_SKEW_MS
          : LEGACY_TOKEN_REFRESH_INTERVAL_MS;
        scheduleRefresh(Math.max(MIN_TOKEN_REFRESH_DELAY_MS, refreshDelay));
      } catch {
        if (!active) return;

        // 卸载通知组件可立即停止其 unread-count 轮询，避免失效 Token 刷屏。
        setToken(undefined);
        scheduleRefresh(TOKEN_REFRESH_RETRY_MS);
      }
    };

    void refreshToken();

    return () => {
      active = false;
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [currentUser?.memberUuid, loading]);

  if (loading || !currentUser?.memberUuid || !token) {
    return null;
  }

  return <CoreNotificationIcon {...props} token={token} />;
};

export default NotificationIcon;
